export type StandardBookingFieldId =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'address'
  | 'city'
  | 'postalCode'
  | 'notes';

export interface BookingFieldSetting {
  display: boolean;
  required: boolean;
}

export interface CustomBookingFieldSetting extends BookingFieldSetting {
  id: string;
  label: string;
}

export type BookingContactFieldSettings = Record<StandardBookingFieldId, BookingFieldSetting>;

export const standardBookingFieldLabels: Record<StandardBookingFieldId, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phone: 'Phone Number',
  address: 'Address',
  city: 'City',
  postalCode: 'Zip Code',
  notes: 'Notes',
};

export const defaultBookingContactFields: BookingContactFieldSettings = {
  firstName: { display: true, required: true },
  lastName: { display: true, required: true },
  email: { display: true, required: true },
  phone: { display: true, required: false },
  address: { display: false, required: false },
  city: { display: false, required: false },
  postalCode: { display: false, required: false },
  notes: { display: true, required: false },
};

export const defaultCustomBookingFields: CustomBookingFieldSetting[] = Array.from(
  { length: 5 },
  (_, index) => ({
    id: `custom_${index + 1}`,
    label: '',
    display: false,
    required: false,
  })
);

export function mergeBookingContactFields(
  value?: Partial<Record<StandardBookingFieldId, Partial<BookingFieldSetting>>> | null
): BookingContactFieldSettings {
  return Object.fromEntries(
    Object.entries(defaultBookingContactFields).map(([fieldId, defaults]) => {
      const field = value?.[fieldId as StandardBookingFieldId];
      const display = field?.display ?? defaults.display;
      return [
        fieldId,
        {
          display,
          required: display ? field?.required ?? defaults.required : false,
        },
      ];
    })
  ) as BookingContactFieldSettings;
}

export function mergeCustomBookingFields(
  value?: Array<Partial<CustomBookingFieldSetting>> | null
): CustomBookingFieldSetting[] {
  return defaultCustomBookingFields.map((defaults, index) => {
    const field = value?.[index];
    const label = field?.label || '';
    const display = Boolean(field?.display && label.trim());
    return {
      id: field?.id || defaults.id,
      label,
      display,
      required: display ? Boolean(field?.required) : false,
    };
  });
}
